/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  StatusBar,
  ActivityIndicator,
  Modal,
  TextInput,
  Alert,
  Share,
  RefreshControl,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome,
  AntDesign,
  MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ IMPORT NEW LOGO COMPONENT ====================
import { HeaderLogo } from '../../../components/HeaderLogo';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  // Single project endpoints – order matters: working first, fallback second
  projectById: (id) => `${API_BASE_URL}/api/projects/${id}`, // ✅ works
  projectByIdFallback: (id) => `${API_BASE_URL}/api/projects/${id}`, // ❌ may fail
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id')
                      || await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId), company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function ProjectDetailsScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { projectId } = route.params || {};

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [updateText, setUpdateText] = useState('');
  const [projectData, setProjectData] = useState(null);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  // ❌ Removed local companyLogo state – now handled by HeaderLogo

  // Fetch project data when projectId is available
  useEffect(() => {
    if (projectId) {
      fetchProjectDetails();
    } else {
      Alert.alert('Error', 'No project ID provided');
      navigation.goBack();
    }
  }, [projectId]);

  const fetchProjectDetails = async () => {
    setLoading(true);
    let project = null;
    let lastError = null;

    // Try working endpoint first, then fallback
    const endpointsToTry = [
      API_ENDPOINTS.projectById(projectId),        // ✅ working endpoint
      API_ENDPOINTS.projectByIdFallback(projectId), // ❌ fallback (may fail)
    ];

    for (const endpoint of endpointsToTry) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);
        const response = await api.get(endpoint);
        
        // Handle different response formats
        if (response.data?.success && response.data.data) {
          project = response.data.data;
        } else if (response.data && typeof response.data === 'object') {
          project = response.data; // direct object
        }

        if (project) {
          console.log(`✅ Project fetched from ${endpoint}`);
          break; // success, exit loop
        }
      } catch (err) {
        console.log(`❌ Endpoint failed: ${endpoint}`, err.message);
        lastError = err;
      }
    }

    if (project) {
      // Transform to match UI expectations
      const normalizedProject = {
        id: project.id,
        title: project.name || 'Untitled Project',
        description: project.description || '',
        client: project.client_name || project.client_company || 'Unknown Client',
        location: project.location || 'Unknown Location',
        startDate: project.start_date || new Date().toISOString(),
        endDate: project.deadline || project.end_date || new Date().toISOString(),
        budget: project.budget ? String(project.budget) : '0',
        currency: 'GBP',
        progress: project.progress || 0,
        status: project.status || 'Planning',
        priority: project.priority || 'Medium',
        team: Array.isArray(project.team_members) ? project.team_members : [],
        milestones: Array.isArray(project.milestones) ? project.milestones : [],
        risks: Array.isArray(project.risks) ? project.risks : [],
        tags: Array.isArray(project.tags) ? project.tags : [],
        recentUpdates: Array.isArray(project.recent_updates) ? project.recent_updates : [],
      };
      setProjectData(normalizedProject);
    } else {
      console.error('All endpoints failed:', lastError);
      Alert.alert(
        'Error',
        'Failed to load project details. Please check your connection and try again.',
        [{ text: 'OK', onPress: () => navigation.goBack() }]
      );
    }
    setLoading(false);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchProjectDetails();
    setRefreshing(false);
  };

  // Handle back button navigation
  const handleGoBack = () => {
    navigation.goBack();
  };

  // Format date
  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get status color
  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'active':
        return '#10B981';
      case 'planning':
        return '#8B5CF6';
      case 'on hold':
        return '#F59E0B';
      case 'completed':
        return '#0e4e96';
      default:
        return '#64748B';
    }
  };

  // Get priority color
  const getPriorityColor = priority => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#64748B';
    }
  };

  // Get milestone status color
  const getMilestoneStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return '#10B981';
      case 'in progress':
        return '#F59E0B';
      case 'on hold':
        return '#8B5CF6';
      case 'not started':
        return '#64748B';
      default:
        return '#64748B';
    }
  };

  // Handle add update
  const handleAddUpdate = () => {
    if (!projectData) return;

    if (updateText.trim() === '') {
      Alert.alert('Error', 'Please enter an update');
      return;
    }

    const newUpdate = {
      date: new Date().toISOString().split('T')[0],
      update: updateText.trim(),
    };

    const updatedProject = {
      ...projectData,
      recentUpdates: [newUpdate, ...(projectData.recentUpdates || [])],
    };

    setProjectData(updatedProject);
    setUpdateText('');
    setShowAddUpdateModal(false);

    Alert.alert('Success', 'Project update added successfully');
  };

  // Handle edit project
  const handleEditProject = () => {
    if (!projectData) return;
    navigation.navigate('EditProject', { project: projectData });
    setShowOptionsModal(false);
  };

  // Handle delete project
  const handleDeleteProject = () => {
    setShowOptionsModal(false);
    setShowConfirmDeleteModal(true);
  };

  // Confirm delete project
  const confirmDeleteProject = () => {
    setLoading(true);
    // Simulate API call with timeout
    setTimeout(() => {
      setLoading(false);
      setShowConfirmDeleteModal(false);
      Alert.alert('Success', 'Project has been deleted successfully', [
        { text: 'OK', onPress: () => navigation.navigate('ManageProjects') },
      ]);
    }, 1500);
  };

  // Handle share project
  const handleShareProject = async () => {
    if (!projectData) return;
    setShowOptionsModal(false);

    try {
      const result = await Share.share({
        message: `Project: ${projectData.title || 'N/A'}\nClient: ${projectData.client || 'N/A'}\nLocation: ${projectData.location || 'N/A'}\nStatus: ${projectData.status || 'N/A'}\nProgress: ${projectData.progress || 0}%`,
        title: projectData.title || 'Project Details',
      });
    } catch (error) {
      Alert.alert('Error', 'Failed to share project');
    }
  };

  // Navigate to milestones screen
  const navigateToMilestones = () => {
    if (!projectData) return;
    navigation.navigate('ProjectMilestones', { project: projectData });
  };

  // Navigate to team screen
  const navigateToTeam = () => {
    if (!projectData) return;
    navigation.navigate('ProjectTeam', { project: projectData });
  };

  // Navigate to risks screen
  const navigateToRisks = () => {
    if (!projectData) return;
    navigation.navigate('ProjectRisks', { project: projectData });
  };

  // Navigate to analytics screen
  const navigateToAnalytics = () => {
    if (!projectData) return;
    navigation.navigate('ProjectAnalytics', { project: projectData });
  };

  // Render progress bar
  const renderProgressBar = progress => {
    const safeProgress = progress || 0;
    return (
      <View style={styles.progressBarContainer}>
        <View style={styles.progressBar}>
          <View
            style={[
              styles.progressFill,
              {
                width: `${safeProgress}%`,
                backgroundColor:
                  safeProgress < 25
                    ? '#EF4444'
                    : safeProgress < 50
                      ? '#F59E0B'
                      : safeProgress < 75
                        ? '#10B981'
                        : '#0e4e96',
              },
            ]}
          />
        </View>
        <Text style={styles.progressText}>{safeProgress}%</Text>
      </View>
    );
  };

  // Render milestone status
  const renderMilestoneStatus = status => {
    let color = '#64748B';
    let icon = 'circle-outline';

    switch (status?.toLowerCase()) {
      case 'completed':
        color = '#10B981';
        icon = 'check-circle';
        break;
      case 'in progress':
        color = '#F59E0B';
        icon = 'progress-clock';
        break;
      case 'on hold':
        color = '#8B5CF6';
        icon = 'pause-circle';
        break;
      case 'not started':
        color = '#64748B';
        icon = 'circle-outline';
        break;
      default:
        color = '#64748B';
        icon = 'circle-outline';
    }

    return (
      <View style={styles.milestoneStatusContainer}>
        <MaterialCommunityIcons name={icon} size={16} color={color} />
        <Text style={[styles.milestoneStatusText, { color }]}>
          {status || 'Unknown'}
        </Text>
      </View>
    );
  };

  // Render overview tab
  const renderOverviewTab = () => {
    if (!projectData) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading project details...</Text>
        </View>
      );
    }

    return (
      <View style={styles.tabContent}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Project Description</Text>
          <Text style={styles.descriptionText}>
            {projectData.description || 'No description available.'}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key Details</Text>
          <View style={styles.detailsGrid}>
            <View style={styles.detailItem}>
              <Ionicons name="business-outline" size={20} color="#0e4e96" />
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Client</Text>
                <Text style={styles.detailValue}>
                  {projectData.client || 'N/A'}
                </Text>
              </View>
            </View>

            <View style={styles.detailItem}>
              <Ionicons name="location-outline" size={20} color="#0e4e96" />
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Location</Text>
                <Text style={styles.detailValue}>
                  {projectData.location || 'N/A'}
                </Text>
              </View>
            </View>

            <View style={styles.detailItem}>
              <Ionicons name="calendar-outline" size={20} color="#0e4e96" />
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Timeline</Text>
                <Text style={styles.detailValue}>
                  {formatDate(projectData.startDate)} -{' '}
                  {formatDate(projectData.endDate)}
                </Text>
              </View>
            </View>

            <View style={styles.detailItem}>
              <Ionicons name="cash-outline" size={20} color="#0e4e96" />
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Budget</Text>
                <Text style={styles.detailValue}>
                  {projectData.budget || 'N/A'}
                </Text>
              </View>
            </View>

            <View style={styles.detailItem}>
              <MaterialCommunityIcons
                name="flag"
                size={20}
                color={getPriorityColor(projectData.priority)}
              />
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Priority</Text>
                <Text
                  style={[
                    styles.detailValue,
                    { color: getPriorityColor(projectData.priority) },
                  ]}
                >
                  {projectData.priority || 'N/A'}
                </Text>
              </View>
            </View>

            <View style={styles.detailItem}>
              <MaterialCommunityIcons
                name="progress-check"
                size={20}
                color="#0e4e96"
              />
              <View style={styles.detailContent}>
                <Text style={styles.detailLabel}>Progress</Text>
                <View style={{ width: '100%' }}>
                  {renderProgressBar(projectData.progress)}
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Team</Text>
            <TouchableOpacity
              style={styles.viewAllButton}
              onPress={navigateToTeam}
            >
              <Text style={styles.viewAllButtonText}>View All</Text>
              <Ionicons name="chevron-forward" size={16} color="#0e4e96" />
            </TouchableOpacity>
          </View>

          {projectData.team && projectData.team.length > 0 ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.teamContainer}
            >
              {projectData.team.map((member, index) => (
                <View
                  key={member.id || `team-${index}`}
                  style={styles.teamMemberCard}
                >
                  <Image
                    source={{
                      uri:
                        member.avatar ||
                        'https://randomuser.me/api/portraits/lego/1.jpg',
                    }}
                    style={styles.teamMemberAvatar}
                  />
                  <Text style={styles.teamMemberName}>
                    {member.name || 'Unknown'}
                  </Text>
                  <Text style={styles.teamMemberRole}>
                    {member.role || 'Team Member'}
                  </Text>
                </View>
              ))}
            </ScrollView>
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons
                name="account-group"
                size={30}
                color="#CBD5E1"
              />
              <Text style={styles.emptyStateText}>
                No team members assigned
              </Text>
            </View>
          )}
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Milestones</Text>
            <TouchableOpacity
              style={styles.viewAllButton}
              onPress={navigateToMilestones}
            >
              <Text style={styles.viewAllButtonText}>View All</Text>
              <Ionicons name="chevron-forward" size={16} color="#0e4e96" />
            </TouchableOpacity>
          </View>

          {projectData.milestones && projectData.milestones.length > 0 ? (
            <>
              {projectData.milestones.slice(0, 3).map((milestone, index) => (
                <View
                  key={milestone.id || `milestone-${index}`}
                  style={styles.milestoneItem}
                >
                  <View style={styles.milestoneHeader}>
                    <Text style={styles.milestoneTitle}>
                      {milestone.title || 'Untitled Milestone'}
                    </Text>
                    {renderMilestoneStatus(milestone.status)}
                  </View>
                  <View style={styles.milestoneDueDate}>
                    <Ionicons
                      name="calendar-outline"
                      size={14}
                      color="#64748B"
                    />
                    <Text style={styles.milestoneDueDateText}>
                      Due: {formatDate(milestone.dueDate)}
                    </Text>
                  </View>
                </View>
              ))}

              {projectData.milestones.length > 3 && (
                <Text style={styles.moreItemsText}>
                  +{projectData.milestones.length - 3} more milestones
                </Text>
              )}
            </>
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons
                name="flag-checkered"
                size={30}
                color="#CBD5E1"
              />
              <Text style={styles.emptyStateText}>No milestones defined</Text>
            </View>
          )}
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Risk Assessment</Text>
            <TouchableOpacity
              style={styles.viewAllButton}
              onPress={navigateToRisks}
            >
              <Text style={styles.viewAllButtonText}>View All</Text>
              <Ionicons name="chevron-forward" size={16} color="#0e4e96" />
            </TouchableOpacity>
          </View>

          {projectData.risks && projectData.risks.length > 0 ? (
            <>
              {projectData.risks.slice(0, 2).map((risk, index) => (
                <View
                  key={risk.id || `risk-${index}`}
                  style={styles.riskItem}
                >
                  <View style={styles.riskHeader}>
                    <View
                      style={[
                        styles.severityBadge,
                        {
                          backgroundColor:
                            risk.severity === 'High'
                              ? '#FEE2E2'
                              : risk.severity === 'Medium'
                                ? '#FEF3C7'
                                : '#DCFCE7',
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.severityText,
                          {
                            color:
                              risk.severity === 'High'
                                ? '#B91C1C'
                                : risk.severity === 'Medium'
                                  ? '#B45309'
                                  : '#166534',
                          },
                        ]}
                      >
                        {risk.severity || 'Unknown'}
                      </Text>
                    </View>
                  </View>
                  <Text style={styles.riskDescription}>
                    {risk.description || 'No description'}
                  </Text>
                  <View style={styles.riskMitigation}>
                    <Text style={styles.riskMitigationLabel}>Mitigation:</Text>
                    <Text style={styles.riskMitigationText}>
                      {risk.mitigation || 'No mitigation plan'}
                    </Text>
                  </View>
                </View>
              ))}

              {projectData.risks.length > 2 && (
                <Text style={styles.moreItemsText}>
                  +{projectData.risks.length - 2} more risks
                </Text>
              )}
            </>
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons
                name="alert-circle-outline"
                size={30}
                color="#CBD5E1"
              />
              <Text style={styles.emptyStateText}>No risks identified</Text>
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Tags</Text>
          {projectData.tags && projectData.tags.length > 0 ? (
            <View style={styles.tagsContainer}>
              {projectData.tags.map((tag, index) => (
                <View key={`tag-${index}`} style={styles.tag}>
                  <Text style={styles.tagText}>{tag}</Text>
                </View>
              ))}
            </View>
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons
                name="tag-outline"
                size={30}
                color="#CBD5E1"
              />
              <Text style={styles.emptyStateText}>No tags added</Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  // Render updates tab
  const renderUpdatesTab = () => {
    if (!projectData) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading project updates...</Text>
        </View>
      );
    }

    return (
      <View style={styles.tabContent}>
        <TouchableOpacity
          style={styles.addUpdateButton}
          onPress={() => setShowAddUpdateModal(true)}
        >
          <Ionicons name="add-circle-outline" size={20} color="#fff" />
          <Text style={styles.addUpdateButtonText}>Add Update</Text>
        </TouchableOpacity>

        {!projectData.recentUpdates ||
        projectData.recentUpdates.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons
              name="text-box-outline"
              size={40}
              color="#CBD5E1"
            />
            <Text style={styles.emptyStateText}>No updates yet</Text>
          </View>
        ) : (
          <View style={styles.updatesList}>
            {projectData.recentUpdates.map((update, index) => (
              <View key={`update-${index}`} style={styles.updateItem}>
                <Text style={styles.updateDate}>{formatDate(update.date)}</Text>
                <Text style={styles.updateText}>
                  {update.update || 'No details'}
                </Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  // Render documents tab
  const renderDocumentsTab = () => {
    return (
      <View style={styles.tabContent}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons
            name="file-document-outline"
            size={40}
            color="#CBD5E1"
          />
          <Text style={styles.emptyStateText}>No documents available</Text>
        </View>
      </View>
    );
  };

  // Render active tab content
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'updates':
        return renderUpdatesTab();
      case 'documents':
        return renderDocumentsTab();
      default:
        return renderOverviewTab();
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <Ionicons name="chevron-back" size={24} color="#fff" />
          </TouchableOpacity>

          <View style={styles.headerCenter}>
            {/* ✅ Use HeaderLogo component */}
            <HeaderLogo size={30} />
            <Text style={styles.headerTitle}>Project Details</Text>
          </View>

          <View style={styles.headerRight}>
            <TouchableOpacity style={styles.headerButton}>
              <Ionicons name="ellipsis-vertical" size={24} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading project details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!projectData) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <Ionicons name="chevron-back" size={24} color="#fff" />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            {/* ✅ Use HeaderLogo component */}
            <HeaderLogo size={30} />
            <Text style={styles.headerTitle}>Project Details</Text>
          </View>
          <View style={styles.headerRight} />
        </View>
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="alert-circle-outline" size={60} color="#EF4444" />
          <Text style={styles.errorText}>Project not found</Text>
          <TouchableOpacity style={styles.errorButton} onPress={handleGoBack}>
            <Text style={styles.errorButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          {/* ✅ Use HeaderLogo component */}
          <HeaderLogo size={30} />
          <Text style={styles.headerTitle}>Project Details</Text>
        </View>

        <View style={styles.headerRight}>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowOptionsModal(true)}
          >
            <Ionicons name="ellipsis-vertical" size={24} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Project Title and Status */}
      <View style={styles.projectHeader}>
        <View style={styles.projectTitleContainer}>
          <Text style={styles.projectTitle}>
            {projectData?.title || 'Untitled Project'}
          </Text>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: getStatusColor(projectData?.status) },
            ]}
          >
            <Text style={styles.statusText}>
              {projectData?.status || 'Unknown'}
            </Text>
          </View>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[
            styles.tabButton,
            activeTab === 'overview' && styles.tabButtonActive,
          ]}
          onPress={() => setActiveTab('overview')}
        >
          <Text
            style={[
              styles.tabButtonText,
              activeTab === 'overview' && styles.tabButtonTextActive,
            ]}
          >
            Overview
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.tabButton,
            activeTab === 'updates' && styles.tabButtonActive,
          ]}
          onPress={() => setActiveTab('updates')}
        >
          <Text
            style={[
              styles.tabButtonText,
              activeTab === 'updates' && styles.tabButtonTextActive,
            ]}
          >
            Updates
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.tabButton,
            activeTab === 'documents' && styles.tabButtonActive,
          ]}
          onPress={() => setActiveTab('documents')}
        >
          <Text
            style={[
              styles.tabButtonText,
              activeTab === 'documents' && styles.tabButtonTextActive,
            ]}
          >
            Documents
          </Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      <ScrollView
        style={styles.contentContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />
        }
      >
        {renderActiveTabContent()}
      </ScrollView>

      {/* Quick Action Buttons */}
      <View style={styles.quickActionsContainer}>
        <TouchableOpacity
          style={styles.quickActionButton}
          onPress={navigateToTeam}
        >
          <Ionicons name="people" size={24} color="#0e4e96" />
          <Text style={styles.quickActionText}>Team</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.quickActionButton}
          onPress={navigateToMilestones}
        >
          <MaterialCommunityIcons
            name="flag-checkered"
            size={24}
            color="#0e4e96"
          />
          <Text style={styles.quickActionText}>Milestones</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.quickActionButton}
          onPress={navigateToRisks}
        >
          <MaterialCommunityIcons
            name="alert-circle"
            size={24}
            color="#0e4e96"
          />
          <Text style={styles.quickActionText}>Risks</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.quickActionButton}
          onPress={navigateToAnalytics}
        >
          <Ionicons name="stats-chart" size={24} color="#0e4e96" />
          <Text style={styles.quickActionText}>Analytics</Text>
        </TouchableOpacity>
      </View>

      {/* Add Update Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showAddUpdateModal}
        onRequestClose={() => setShowAddUpdateModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Project Update</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowAddUpdateModal(false)}
              >
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <TextInput
              style={styles.updateInput}
              value={updateText}
              onChangeText={setUpdateText}
              placeholder="Enter project update..."
              placeholderTextColor="#94A3B8"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />

            <TouchableOpacity
              style={styles.addButton}
              onPress={handleAddUpdate}
            >
              <Text style={styles.addButtonText}>Add Update</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Options Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showOptionsModal}
        onRequestClose={() => setShowOptionsModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowOptionsModal(false)}
        >
          <View style={styles.optionsModalContent}>
            <TouchableOpacity
              style={styles.optionItem}
              onPress={handleEditProject}
            >
              <Ionicons name="create-outline" size={24} color="#0e4e96" />
              <Text style={styles.optionText}>Edit Project</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.optionItem}
              onPress={handleShareProject}
            >
              <Ionicons name="share-outline" size={24} color="#0e4e96" />
              <Text style={styles.optionText}>Share Project</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.optionItem}
              onPress={handleDeleteProject}
            >
              <Ionicons name="trash-outline" size={24} color="#EF4444" />
              <Text style={[styles.optionText, { color: '#EF4444' }]}>
                Delete Project
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showConfirmDeleteModal}
        onRequestClose={() => setShowConfirmDeleteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmModalContent}>
            <View style={styles.confirmModalIcon}>
              <Ionicons name="warning" size={40} color="#EF4444" />
            </View>

            <Text style={styles.confirmModalTitle}>Delete Project?</Text>
            <Text style={styles.confirmModalText}>
              This action cannot be undone. All project data will be permanently
              deleted.
            </Text>

            <View style={styles.confirmModalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowConfirmDeleteModal(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.deleteButton}
                onPress={confirmDeleteProject}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size={20} color="#fff" />
                ) : (
                  <Text style={styles.deleteButtonText}>Delete</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 5,
  },
  headerCenter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  // ❌ Removed headerLogo style – it's now handled by HeaderLogo component
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  headerRight: {
    width: 40,
    alignItems: 'flex-end',
  },
  headerButton: {
    padding: 5,
  },
  projectHeader: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 20,
    paddingBottom: 15,
  },
  projectTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  projectTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
    marginRight: 10,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#fff',
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
  },
  tabButtonActive: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  tabButtonTextActive: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  contentContainer: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 80,
  },
  tabContent: {
    flex: 1,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 5,
    elevation: 1,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewAllButtonText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '500',
  },
  descriptionText: {
    fontSize: 14,
    color: '#334155',
    lineHeight: 20,
  },
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  detailItem: {
    width: '50%',
    paddingHorizontal: 8,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  detailContent: {
    marginLeft: 8,
    flex: 1,
  },
  detailLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 14,
    color: '#0f172a',
    fontWeight: '500',
  },
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  progressBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#e2e8f0',
    borderRadius: 3,
    marginRight: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
    width: 35,
    textAlign: 'right',
  },
  teamContainer: {
    paddingVertical: 8,
  },
  teamMemberCard: {
    alignItems: 'center',
    marginRight: 16,
    width: 80,
  },
  teamMemberAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginBottom: 8,
  },
  teamMemberName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0f172a',
    textAlign: 'center',
  },
  teamMemberRole: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 2,
  },
  milestoneItem: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  milestoneTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0f172a',
    flex: 1,
    marginRight: 8,
  },
  milestoneStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  milestoneStatusText: {
    fontSize: 12,
    marginLeft: 4,
  },
  milestoneDueDate: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  milestoneDueDateText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  moreItemsText: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
  },
  riskItem: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  riskHeader: {
    marginBottom: 8,
  },
  severityBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  severityText: {
    fontSize: 12,
    fontWeight: '500',
  },
  riskDescription: {
    fontSize: 14,
    color: '#0f172a',
    marginBottom: 8,
  },
  riskMitigation: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  riskMitigationLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
    marginRight: 4,
  },
  riskMitigationText: {
    fontSize: 12,
    color: '#64748B',
    flex: 1,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tag: {
    backgroundColor: '#f1f5f9',
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginRight: 8,
    marginBottom: 8,
  },
  tagText: {
    fontSize: 12,
    color: '#64748B',
  },
  addUpdateButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  addUpdateButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 8,
  },
  updatesList: {
    marginTop: 8,
  },
  updateItem: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#0e4e96',
  },
  updateDate: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  updateText: {
    fontSize: 14,
    color: '#0f172a',
    lineHeight: 20,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 8,
  },
  quickActionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
  },
  quickActionButton: {
    alignItems: 'center',
  },
  quickActionText: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    padding: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
  },
  modalCloseButton: {
    padding: 4,
  },
  updateInput: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0f172a',
    minHeight: 120,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  addButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  optionsModalContent: {
    position: 'absolute',
    top: 70,
    right: 20,
    backgroundColor: '#fff',
    borderRadius: 12,
    width: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 5,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  optionText: {
    fontSize: 16,
    color: '#0f172a',
    marginLeft: 12,
  },
  confirmModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '80%',
    padding: 24,
    alignItems: 'center',
  },
  confirmModalIcon: {
    marginBottom: 16,
  },
  confirmModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  confirmModalText: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
  },
  confirmModalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  cancelButtonText: {
    color: '#64748B',
    fontSize: 16,
    fontWeight: '600',
  },
  deleteButton: {
    flex: 1,
    backgroundColor: '#EF4444',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginTop: 16,
    marginBottom: 20,
  },
  errorButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  errorButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});